import React from 'react';
import { ArrowRight, BarChart3, CheckCircle, FileText, ShieldCheck, Sparkles, Target, Users } from 'lucide-react';
import { motion } from 'framer-motion';

const heroImage = 'https://customer-assets.emergentagent.com/job_b7df7e6c-e242-4f3e-81af-66fcd6ed0832/artifacts/t1f8bpw4_image%20%2825%29.png';

const proofPoints = [
  'One safe workflow to test first',
  'Human-review guardrails',
  '14-day proof plan',
];

const audiences = [
  'Accounting and bookkeeping firms',
  'Consulting and advisory firms',
  'Insurance agencies',
  'Financial planning and wealth advisory firms',
  'Fractional CFO and operations firms',
  'Owner-led B2B service firms',
];

const auditChecks = [
  { icon: Target, title: 'Where AI belongs first', body: 'Find the workflow that is frequent, useful, and safe enough to test.' },
  { icon: ShieldCheck, title: 'Where AI should not go yet', body: 'Name the judgment, privacy, and client-trust boundaries before tools enter the workflow.' },
  { icon: BarChart3, title: 'What it may be worth', body: 'Estimate time saved and value created without promising fake certainty.' },
  { icon: FileText, title: 'What to do next', body: 'Leave with a small proof plan instead of an overwhelming transformation map.' },
];

const firmExamples = [
  {
    title: 'Accounting and bookkeeping',
    image: 'Ledger-ready intake',
    pilots: ['Client document checklist', 'Month-end close status summary', 'Draft questions from missing documents'],
    boundary: 'No final tax, accounting, or compliance judgment without expert review.',
  },
  {
    title: 'Consulting and advisory',
    image: 'Client workbench',
    pilots: ['Meeting notes to action items', 'Proposal drafts from approved templates', 'Client research briefs'],
    boundary: 'No final strategic recommendation without expert review.',
  },
  {
    title: 'Insurance agencies',
    image: 'Renewal control room',
    pilots: ['Renewal follow-up lists', 'Client intake summaries', 'Producer reminders'],
    boundary: 'No final coverage or claims guidance without licensed review.',
  },
  {
    title: 'Financial planning and wealth advisory',
    image: 'Review prep desk',
    pilots: ['Meeting prep summaries', 'Follow-up task lists', 'Annual review checklists'],
    boundary: 'No investment advice, suitability recommendation, or financial-plan conclusion.',
  },
];

const deliverables = [
  'AI readiness snapshot',
  'Best first workflow candidate',
  'Conservative value estimate',
  'Risk and human-review flags',
  '14-day proof plan',
  'Build-vs-buy guidance',
  'What not to automate yet',
  'Suggested next step',
];

function Pill({ children, className = '' }) {
  return <span className={`inline-flex rounded-full px-4 py-2 text-xs font-black uppercase tracking-[0.22em] ${className}`}>{children}</span>;
}

function ProfessionalFirmsLandingPage() {
  return (
    <main className="min-h-screen bg-white text-[#1c334a]">
      <section className="relative flex min-h-[92vh] items-center overflow-hidden bg-[#1c334a]">
        <div className="absolute inset-0 bg-cover bg-right" style={{ backgroundImage: `url(${heroImage})` }} />
        <div className="absolute inset-0 bg-gradient-to-r from-[#1c334a] via-[#1c334a]/90 to-[#1c334a]/35" />
        <div className="absolute right-8 top-32 hidden h-72 w-72 rounded-full bg-[#049aaa]/30 blur-3xl lg:block" />

        <div className="container relative z-10 mx-auto max-w-screen-xl px-5 py-16 sm:px-8 lg:py-24">
          <div className="mb-12 flex flex-col gap-5 sm:flex-row sm:items-center sm:justify-between">
            <img src="/trilogy-one-logo.png" alt="Trilogy One Consulting" className="h-12 w-fit rounded bg-white/95 p-2" onError={(event) => { event.currentTarget.style.display = 'none'; }} />
            <Pill className="border border-white/40 bg-white/10 text-white">Professional firms audit</Pill>
          </div>

          <div className="grid gap-10 lg:grid-cols-[1fr_0.78fr] lg:items-center">
            <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} className="max-w-3xl space-y-8">
              <Pill className="bg-[#d8e005] text-[#1c334a]">Responsible AI first step</Pill>
              <div className="space-y-5">
                <h1 className="text-4xl font-black leading-tight text-white sm:text-6xl lg:text-7xl">
                  You built the firm on trust. Do not adopt AI by guessing.
                </h1>
                <p className="max-w-2xl text-lg leading-8 text-white/90 sm:text-xl">
                  Find the first safe, useful AI workflow for your professional firm without risking client trust, overwhelming your team, or chasing hype.
                </p>
              </div>

              <div className="grid gap-3 sm:grid-cols-3">
                {proofPoints.map((point) => (
                  <div key={point} className="flex items-center gap-3 rounded-2xl border border-white/15 bg-white/10 p-4 text-sm font-semibold text-white backdrop-blur">
                    <CheckCircle className="h-5 w-5 shrink-0 text-[#d8e005]" />
                    <span>{point}</span>
                  </div>
                ))}
              </div>

              <div className="flex flex-col gap-4 sm:flex-row">
                <a href="/audit/checkout" className="inline-flex items-center justify-center rounded-2xl bg-[#d8e005] px-7 py-4 font-black text-[#1c334a] shadow-xl transition hover:-translate-y-0.5 hover:bg-white">
                  Take the $9 digital audit <ArrowRight className="ml-2 h-5 w-5" />
                </a>
                <a href="https://calc.trilogyoneconsulting.com/" className="inline-flex items-center justify-center rounded-2xl border-2 border-white/70 px-7 py-4 font-black text-white transition hover:bg-white hover:text-[#1c334a]">
                  Estimate your AI opportunity first
                </a>
              </div>
            </motion.div>

            <motion.div initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.15 }} className="hidden lg:block">
              <div className="rounded-[2rem] border border-white/20 bg-white/95 p-6 shadow-2xl">
                <div className="rounded-3xl bg-[#f7fbff] p-5">
                  <p className="text-xs font-black uppercase tracking-[0.22em] text-[#049aaa]">Sample audit result</p>
                  <h2 className="mt-3 text-2xl font-black text-[#1c334a]">Best first workflow</h2>
                  <p className="mt-2 text-slate-700">Client intake follow-up, with human review before anything leaves the firm.</p>
                </div>
                <div className="mt-5 space-y-3">
                  {['Clear input documents', 'Low-risk internal draft', 'Owner-approved next step'].map((item) => (
                    <div key={item} className="flex items-center gap-3 rounded-2xl border border-[#049aaa]/15 p-4 text-sm font-bold text-[#1c334a]">
                      <CheckCircle className="h-5 w-5 text-[#049aaa]" />
                      {item}
                    </div>
                  ))}
                </div>
                <div className="mt-5 rounded-2xl bg-[#1c334a] p-5 text-white">
                  <p className="text-sm font-bold text-white/70">14-day proof plan</p>
                  <div className="mt-4 h-3 rounded-full bg-white/15">
                    <div className="h-3 w-2/3 rounded-full bg-[#d8e005]" />
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="bg-[#f7fbff] py-12 sm:py-16">
        <div className="container mx-auto max-w-screen-xl px-5 sm:px-8">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {audiences.map((item) => (
              <div key={item} className="rounded-3xl border border-[#049aaa]/10 bg-white p-5 shadow-sm">
                <CheckCircle className="mb-4 h-7 w-7 text-[#049aaa]" />
                <p className="font-bold text-[#1c334a]">{item}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 sm:py-24">
        <div className="container mx-auto max-w-screen-xl px-5 sm:px-8">
          <div className="grid gap-10 lg:grid-cols-[0.9fr_1.1fr] lg:items-center">
            <div className="space-y-6">
              <Pill className="bg-[#049aaa]/10 text-[#049aaa]">Why this matters</Pill>
              <h2 className="text-3xl font-black leading-tight sm:text-5xl">AI is moving fast. Your reputation is too valuable to experiment carelessly.</h2>
              <p className="text-lg leading-8 text-slate-700">
                Most firm owners do not need another generic AI tool. They need to know where AI can safely help the team without touching the judgment, confidentiality, and client trust the business depends on.
              </p>
              <p className="rounded-3xl bg-[#1c334a] p-6 text-lg font-semibold leading-8 text-white">
                The audit helps identify one practical workflow worth testing first, along with the places AI should not be used without human review.
              </p>
            </div>
            <div className="relative overflow-hidden rounded-[2rem] bg-[#1c334a] p-6 shadow-2xl sm:p-8">
              <div className="absolute -right-20 -top-20 h-56 w-56 rounded-full bg-[#d8e005]/25 blur-3xl" />
              <img src="/roadmap-phases.png" alt="Audit, implement, and optimize roadmap phases" className="relative z-10 w-full rounded-3xl bg-white/95 p-4 shadow-xl" />
              <div className="relative z-10 mt-6 grid gap-4 sm:grid-cols-3">
                {['Audit', 'Implement', 'Optimize'].map((step) => (
                  <div key={step} className="rounded-2xl bg-white/10 p-4 text-center font-black text-white">{step}</div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-[#1c334a] py-16 text-white sm:py-24">
        <div className="container mx-auto max-w-screen-xl px-5 sm:px-8">
          <div className="mx-auto max-w-3xl text-center">
            <Pill className="bg-[#d8e005] text-[#1c334a]">What the audit checks</Pill>
            <h2 className="mt-6 text-3xl font-black leading-tight sm:text-5xl">Find one safe, useful workflow before you buy or build anything.</h2>
          </div>
          <div className="mt-12 grid gap-5 md:grid-cols-2 lg:grid-cols-4">
            {auditChecks.map(({ icon: Icon, title, body }) => (
              <article key={title} className="rounded-[1.75rem] border border-white/10 bg-white/10 p-6 backdrop-blur">
                <Icon className="h-10 w-10 text-[#d8e005]" />
                <h3 className="mt-5 text-xl font-black">{title}</h3>
                <p className="mt-3 leading-7 text-white/80">{body}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 sm:py-24">
        <div className="container mx-auto max-w-screen-xl px-5 sm:px-8">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
            <div className="max-w-2xl">
              <Pill className="bg-[#049aaa]/10 text-[#049aaa]">Examples by firm type</Pill>
              <h2 className="mt-5 text-3xl font-black leading-tight sm:text-5xl">Different firms need different first pilots.</h2>
            </div>
            <p className="max-w-md text-slate-700">The audit narrows the first step so the team can test something useful without gambling with client trust.</p>
          </div>

          <div className="mt-10 grid gap-6 md:grid-cols-2">
            {firmExamples.map((example, index) => (
              <article key={example.title} className={`overflow-hidden rounded-[2rem] border bg-white shadow-sm ${index % 3 === 0 ? 'border-[#d8e005]' : 'border-slate-100'}`}>
                <div className={`p-6 text-white ${index % 2 === 0 ? 'bg-[#1c334a]' : 'bg-[#049aaa]'}`}>
                  <div className="mb-4 inline-flex rounded-full bg-white/15 px-4 py-2 text-xs font-black uppercase tracking-widest">{example.image}</div>
                  <h3 className="text-2xl font-black">{example.title}</h3>
                </div>
                <div className="p-6">
                  <ul className="space-y-3">
                    {example.pilots.map((pilot) => (
                      <li key={pilot} className="flex gap-3 text-slate-700"><CheckCircle className="mt-0.5 h-5 w-5 shrink-0 text-[#049aaa]" />{pilot}</li>
                    ))}
                  </ul>
                  <p className="mt-6 rounded-2xl bg-[#f7fbff] p-4 text-sm font-bold leading-6 text-[#1c334a]">Human-review boundary: {example.boundary}</p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-[#f7fbff] py-16 sm:py-24">
        <div className="container mx-auto max-w-screen-xl px-5 sm:px-8">
          <div className="grid gap-10 lg:grid-cols-[0.85fr_1.15fr] lg:items-center">
            <div>
              <Pill className="bg-white text-[#049aaa]">What you receive</Pill>
              <h2 className="mt-5 text-3xl font-black leading-tight sm:text-5xl">A clear first step, not an overwhelming transformation plan.</h2>
              <p className="mt-5 text-lg leading-8 text-slate-700">AI can draft, summarize, classify, remind, and report. Your team keeps responsibility for advice, approvals, promises, and client-facing judgment.</p>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              {deliverables.map((item) => (
                <div key={item} className="rounded-3xl bg-white p-5 shadow-sm">
                  <Sparkles className="mb-3 h-6 w-6 text-[#049aaa]" />
                  <p className="font-black text-[#1c334a]">{item}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="relative overflow-hidden bg-[#1c334a] py-16 text-white sm:py-24">
        <div className="absolute left-0 top-0 h-80 w-80 rounded-full bg-[#049aaa]/20 blur-3xl" />
        <div className="container relative z-10 mx-auto max-w-screen-xl px-5 text-center sm:px-8">
          <Users className="mx-auto h-12 w-12 text-[#d8e005]" />
          <h2 className="mx-auto mt-6 max-w-4xl text-3xl font-black leading-tight sm:text-5xl">Start small. Prove one workflow. Expand only if it works.</h2>
          <p className="mx-auto mt-5 max-w-2xl text-lg leading-8 text-white/80">Take the AI Opportunity Audit and get a practical first-step recommendation for your firm.</p>
          <a href="/audit/checkout" className="mt-8 inline-flex items-center justify-center rounded-2xl bg-[#d8e005] px-8 py-4 font-black text-[#1c334a] shadow-xl transition hover:-translate-y-0.5 hover:bg-white">
            Take the digital audit <ArrowRight className="ml-2 h-5 w-5" />
          </a>
        </div>
      </section>
    </main>
  );
}

export default ProfessionalFirmsLandingPage;
